/**
 * Why a LinkedIn import ended in an error, in words a customer can read.
 *
 * Bulk imports (BackgroundTaskUrls) and single-url imports (LinkedinUrlImports) used to end in a
 * bare "Scraping Error" / "Import Error", or not end at all: a url whose task was dropped somewhere
 * in the pipeline stayed "Processing..." forever. Every url now ends in an explicit outcome, and a
 * failed one carries `error_reason`, a machine code written by the pipeline at the point it gave up.
 * These helpers turn that code into a label and apply the one rule the code changes about status.
 *
 * `error_reason` is optional throughout: an API older than this build does not send it, and then
 * every helper here leaves today's display exactly as it was.
 */

/** Human labels for the reason codes the import pipeline writes. An unknown code is shown as itself. */
export const IMPORT_ERROR_REASON_LABELS: Record<string, string> = {
	scrape_retries_exhausted: "gave up after repeated scraping failures",
	enrich_retries_exhausted: "gave up after repeated enrichment failures",
	crash_loop: "the import kept crashing",
	lost_in_transit: "lost between pipeline steps",
	enqueue_failed: "could not be queued",
	dead_lettered: "rejected by the pipeline",
	bad_request: "invalid import request",
	blacklisted: "blacklisted company",
	routing: "routing error",
	vendor_quota: "vendor quota or error",
	not_returned_by_vendors: "no vendor returned the profile",
	xbyte_unresolved: "X-Byte never answered",
	db_error: "database error",
	unparseable_profile: "unreadable profile",
	scrape_error: "scraping error",
	bad_url: "invalid LinkedIn URL",
	no_positions: "no usable positions",
	too_junior: "below MapX's seniority bar",
	excluded_title: "assistant / retired title",
	blacklisted_candidate: "blacklisted candidate",
};

type TReasonCode = string | null | undefined;

type TStatusWithReason = {
	status: string;
	error_reason?: string | null;
};

/** The label for a reason code; the code itself when this build does not know it; "" when there is none. */
export const importErrorReasonLabel = (code: TReasonCode): string => {
	const trimmed = code?.trim();

	if (!trimmed) {
		return "";
	}

	return IMPORT_ERROR_REASON_LABELS[trimmed] ?? trimmed;
};

/** "<message>: <label>" when there is a reason, the message unchanged when there is not. */
export const withImportErrorReason = (message: string, code: TReasonCode): string => {
	const label = importErrorReasonLabel(code);

	return label ? `${message}: ${label}` : message;
};

// A url in one of these statuses has not finished yet - unless it carries a reason (see below).
const UNFINISHED_STATUSES = ["created", "in progress", "scraped"];

/**
 * The status to show for a url, given its reason.
 *
 * The pipeline writes the reason on the row directly and moves the status through a separate event,
 * so for a short while a row can read "In Progress" with a reason already beside it. The reason is
 * the final word: such a url is finished and failed, and counting it as still processing would bring
 * back the endless "N left" this change exists to remove. It reads as the error status it is about
 * to get - "Import Error" once it was scraped (the failure was in enrichment), "Scraping Error"
 * before that. Every other row, and every row without a reason, keeps its status.
 */
export const settledImportUrlStatus = (item: TStatusWithReason): string => {
	const status = item.status ?? "";

	if (!importErrorReasonLabel(item.error_reason)) {
		return status;
	}

	const lowerStatus = status.toLowerCase();

	if (!UNFINISHED_STATUSES.includes(lowerStatus)) {
		return status;
	}

	return lowerStatus === "scraped" ? "Import Error" : "Scraping Error";
};

/** The url (or single-url import) with its settled status; the same object when nothing changes. */
export const settleImportUrl = <T extends TStatusWithReason>(item: T): T => {
	const status = settledImportUrlStatus(item);

	return status === item.status ? item : {...item, status};
};

/** The urls with their settled status; the same objects when nothing changes. */
export const settleImportUrls = <T extends TStatusWithReason>(items: T[]): T[] =>
	items.map(settleImportUrl);

/**
 * A response body as the store should keep it: a single-url import row with its settled status,
 * so the store never says "In Progress" for an import that has failed. The request helpers hand
 * back an error response's body too, which is not an import row (its status, if any, is a number),
 * and that is returned untouched.
 */
export const settledImportResponseData = <T>(data: T): T => {
	const row = data as unknown as TStatusWithReason | null;

	if (!row || typeof row !== "object" || typeof row.status !== "string") {
		return data;
	}

	return settleImportUrl(row) as unknown as T;
};
